(function (root, factory) {
  const dealerPricing = factory(typeof module === 'object' && module.exports ? require('./ai-chat-dealer-pricing-api.js') : root.MarineMaxAIChatDealerPricingApi);
  if (typeof module === 'object' && module.exports)
    module.exports = dealerPricing;
  if (root) root.MarineMaxAIChatDealerPricing = dealerPricing;
})(typeof window !== 'undefined' ? window : null, function (api) {
  'use strict';

  const fieldNames = [
    'firstName',
    'lastName',
    'email',
    'zip',
    'phone',
    'consent',
  ];
  const steps = new Set(['details', 'verification', 'quote', 'error']);

  function normalizeField(name, value) {
    const text = String(value ?? '').replace(/^\s+/, '');
    return name === 'phone' ? text.replace(/\D/g, '').slice(0, 15) : text;
  }

  function validateDetails(values = {}) {
    const errors = {};
    for (const name of ['firstName', 'lastName']) {
      if (!String(values[name] || '').trim())
        errors[name] =
          'Enter your ' + (name === 'firstName' ? 'first' : 'last') + ' name.';
      else if (/^\s/.test(String(values[name])))
        errors[name] = 'Start with a letter, without a leading space.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(values.email || '')))
      errors.email = 'Enter a valid email address.';
    if (!/^\d{5}(?:-\d{4})?$/.test(String(values.zip || '')))
      errors.zip = 'Enter a valid ZIP code.';
    if (!/^\d{7,15}$/.test(String(values.phone || '')))
      errors.phone = 'Enter a valid phone number using numbers only.';
    if (values.consent !== true)
      errors.consent = 'Please accept the terms and consent to communications.';
    if (!['email', 'sms'].includes(values.channel))
      errors.channel = 'Choose email or text message.';
    return { valid: Object.keys(errors).length === 0, errors };
  }

  function isEligible(context = {}, options = {}) {
    if (
      !context ||
      typeof context !== 'object' ||
      !Array.isArray(context.variants) ||
      !options
    )
      return false;
    const enabled =
      Number(options.width) > 1024 ? options.desktop : options.mobile;
    const variantId = String(
      options.variantId ?? context.selectedVariantId ?? '',
    );
    const variant = (context.variants || []).find(
      (item) => String(item.id) === variantId,
    );
    return (
      enabled === true &&
      context.priceHidden === true &&
      context.hasMsrp === true &&
      variant?.hasPrice === true
    );
  }

  function maskDestination(channel, values = {}) {
    if (channel === 'sms')
      return '••• ••• ' + String(values.phone || '').slice(-4);
    const [name = '', domain = ''] = String(values.email || '').split('@');
    return name ? name.charAt(0) + '•••@' + domain : '';
  }

  function createDealerPricingController(options = {}) {
    const chatRoot = options.root;
    const panel = chatRoot?.querySelector('[data-dealer-pricing]');
    if (!panel) return null;
    const documentRef = chatRoot.ownerDocument;
    const windowRef = options.window || documentRef.defaultView;
    const navigation = options.navigation;
    const stateManager = options.stateManager;
    const view = options.view || 'dealer-pricing';
    const now = options.now || Date.now;
    let context;
    try { context = JSON.parse(chatRoot.querySelector('[data-dealer-pricing-config]')?.textContent || '{}'); }
    catch (_) { context = {}; }
    if (!context || typeof context !== 'object' || !Array.isArray(context.variants)) context = {};
    const client = options.client || api.createClient({ baseUrl: context.apiBaseUrl, fetch: windowRef.fetch?.bind(windowRef) });
    let storage;
    try { storage = windowRef.localStorage; } catch (_) { storage = null; }
    const leadSender = options.leadSender || api.createLeadSender({ config: context.hubspot, fetch: windowRef.fetch?.bind(windowRef), storage, now });
    const chips = Array.from(chatRoot.querySelectorAll('[data-dealer-pricing-chip]'));
    const form = panel.querySelector('[data-dealer-details-form]');
    const submit = panel.querySelector('[data-dealer-submit]');
    const verify = panel.querySelector('[data-dealer-verify]');
    const status = panel.querySelector('[data-dealer-status]');
    const digits = Array.from(panel.querySelectorAll('[data-dealer-digit]'));
    const resend = panel.querySelector('[data-dealer-resend]');
    const switchChannel = panel.querySelector('[data-dealer-switch-channel]');
    const download = panel.querySelector('[data-dealer-download]');
    const email = panel.querySelector('[data-dealer-email]');
    const leadRetry = panel.querySelector('[data-dealer-lead-retry]');
    const values = { firstName: '', lastName: '', email: '', zip: '', phone: '', consent: false, channel: 'email', dialCode: '+1' };
    const touched = new Set();
    const cleanups = [];
    let variantId = context.selectedVariantId;
    let currentStep = 'details';
    let busy = false;
    let generation = 0;
    let requestController = null;
    let destroyed = false;
    let issued = null;
    let failedAttempts = 0;
    let authToken = '';
    let tokenExpiresAt = 0;
    let quote = null;
    let pendingFocus = null;

    function listen(target, type, handler) {
      if (!target) return;
      target.addEventListener(type, handler);
      cleanups.push(() => target.removeEventListener(type, handler));
    }
    function setText(selector, text) {
      const target = panel.querySelector(selector);
      if (target) target.textContent = String(text ?? '');
    }
    function setStatus(text) {
      if (status) { status.textContent = text; status.hidden = !text; }
    }
    function canShow() {
      return isEligible(context, {
        desktop: chatRoot.dataset.dealerPricingDesktop === 'true',
        mobile: chatRoot.dataset.dealerPricingMobile === 'true',
        width: windowRef.innerWidth, variantId,
      });
    }
    function hasToken() { return Boolean(authToken && tokenExpiresAt > now()); }
    function clearCode() { digits.forEach((digit) => { digit.value = ''; }); }
    function clearAuthorization() { authToken = ''; tokenExpiresAt = 0; quote = null; }
    function updateValidation() {
      const result = validateDetails(values);
      if (!api.toE164(values.phone, values.dialCode)) {
        result.errors.phone = 'Enter a valid phone number using numbers only.';
        result.valid = false;
      }
      if (submit) submit.disabled = busy || !result.valid;
      for (const name of fieldNames) {
        const input = form?.elements.namedItem(name);
        const message = panel.querySelector('[data-dealer-field-error="' + name + '"]');
        const error = touched.has(name) ? result.errors[name] || '' : '';
        input?.setAttribute('aria-invalid', String(Boolean(error)));
        if (message) { message.textContent = error; message.hidden = !error; }
      }
      return result;
    }
    function syncButtons() {
      updateValidation();
      if (verify) {
        verify.disabled = busy || (!hasToken() && (!issued || !digits.every((digit) => /^\d$/.test(digit.value))));
        verify.textContent = hasToken() ? 'Try Again' : 'Verify & Unlock Pricing';
      }
      if (resend) resend.disabled = busy;
      if (switchChannel) switchChannel.disabled = busy;
      if (download) download.disabled = busy || !quote;
      if (email) email.disabled = busy || !quote;
      for (const input of panel.querySelectorAll('input, select')) input.disabled = busy;
      panel.setAttribute('aria-busy', String(busy));
    }
    function writeValues() {
      for (const name of fieldNames) {
        const input = form?.elements.namedItem(name);
        if (!input) continue;
        if (name === 'consent') input.checked = values.consent;
        else input.value = values[name];
      }
      for (const radio of panel.querySelectorAll('[name="channel"]')) radio.checked = radio.value === values.channel;
      const dial = form?.elements.namedItem('dialCode');
      if (dial) dial.value = values.dialCode;
      syncButtons();
    }
    function renderQuote() {
      if (!quote) return;
      const formatter = new Intl.NumberFormat(documentRef.documentElement.lang || 'en-US', { style: 'currency', currency: quote.pricing.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 });
      const money = (number) => formatter.format(number);
      setText('[data-dealer-prepared]', 'Prepared for ' + [values.firstName, values.lastName].filter(Boolean).join(' ') + ' · Stock ' + quote.boat.stock_number);
      setText('[data-dealer-msrp]', money(quote.pricing.msrp));
      setText('[data-dealer-price]', money(quote.pricing.one_price));
      setText('[data-dealer-savings]', money(quote.pricing.savings));
      const savings = panel.querySelector('[data-dealer-savings-row]');
      if (savings) savings.hidden = quote.pricing.savings === 0;
      const date = new Date(quote.pricing.valid_until + 'T12:00:00Z');
      setText('[data-dealer-valid-until]', 'Valid until ' + date.toLocaleDateString(documentRef.documentElement.lang || 'en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' }));
      const dealer = quote.dealer || {};
      setText('[data-dealer-dealer]', [dealer.name, dealer.phone].filter(Boolean).join(' · '));
      setText('[data-dealer-address]', dealer.address || '');
      const dealerWrap = panel.querySelector('[data-dealer-dealer-wrap]');
      if (dealerWrap) dealerWrap.hidden = !dealer.name && !dealer.phone;
      for (const [selector, value, prefix] of [
        ['[data-dealer-contact-email]', dealer.sales_contact_email, 'mailto:'],
        ['[data-dealer-location]', dealer.location_url, ''],
      ]) {
        const link = panel.querySelector(selector);
        if (!link) continue;
        const href = prefix ? (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '') ? prefix + value : '') : api.safeUrl(value);
        link.hidden = !href;
        if (href) link.setAttribute('href', href);
        else link.removeAttribute('href');
      }
    }
    function render(nextStep, focus = false) {
      if (!steps.has(nextStep) || (nextStep === 'quote' && !quote)) return false;
      currentStep = nextStep;
      panel.hidden = false;
      chatRoot.classList.add('ai-chat--dealer-pricing-mode');
      for (const selector of ['.ai-chat__suggest', '.ai-chat__hubspot', '.ai-chat__chat', '.ai-chat__input-wrapper']) {
        const target = chatRoot.querySelector(selector);
        if (target) target.style.display = 'none';
      }
      const back = chatRoot.querySelector('.ai-chat__back');
      if (back) back.style.display = 'flex';
      for (const step of panel.querySelectorAll('[data-dealer-step]')) step.hidden = step.dataset.dealerStep !== nextStep;
      stateManager.setView(view);
      navigation.syncHeaderMode();
      setStatus('');
      setText('[data-dealer-destination]', maskDestination(issued?.channel || values.channel, values));
      if (switchChannel) switchChannel.textContent = values.channel === 'email' ? 'Send the code through text message instead.' : 'Email me instead.';
      if (nextStep === 'quote') renderQuote();
      syncButtons();
      panel.scrollTop = 0;
      if (focus) {
        const activeStep = panel.querySelector('[data-dealer-step="' + nextStep + '"]');
        const target = activeStep?.querySelector('input:not([type="hidden"]), [data-dealer-close]') || activeStep?.querySelector('h3');
        if (target?.disabled) pendingFocus = target;
        else target?.focus();
      }
      return true;
    }
    function cancelPending() {
      generation++;
      requestController?.abort();
      requestController = null;
      pendingFocus = null;
      busy = false;
      syncButtons();
    }
    function hide() {
      cancelPending();
      panel.hidden = true;
      chatRoot.classList.remove('ai-chat--dealer-pricing-mode');
    }
    function back() {
      const wasBusy = busy;
      hide();
      if (wasBusy) setStatus('');
      navigation.showChat({ allowDesktopFormExit: true });
      chips.find((chip) => !chip.hidden)?.focus();
      return true;
    }
    function close() {
      clearCode(); clearAuthorization(); issued = null; failedAttempts = 0;
      currentStep = 'details';
      values.consent = false;
      values.channel = 'email';
      setStatus(''); writeValues();
      return back();
    }
    function syncEligibility() {
      const eligible = canShow();
      for (const chip of chips) chip.hidden = !eligible;
      chatRoot.dataset.showUnlockPricing = String(eligible);
      if (!eligible && !panel.hidden) close();
      if (typeof options.onEligibilityChange === 'function') options.onEligibilityChange(eligible);
      return eligible;
    }
    function open() {
      if (!canShow()) return false;
      const message = status && !status.hidden ? status.textContent : '';
      cancelPending(); writeValues();
      navigation.saveState();
      if (currentStep === 'quote' && !hasToken()) {
        expiredSession();
        return true;
      }
      const rendered = render(currentStep, true);
      if (message) setStatus(message);
      return rendered;
    }
    function showError(heading, message) {
      render('error', true);
      setText('[data-dealer-error-title]', heading);
      setText('[data-dealer-error]', message);
    }
    function expiredSession() {
      clearAuthorization(); clearCode(); issued = null;
      render('verification', true);
      setStatus('Your session has expired. Please request a new code to continue.');
    }
    function handleError(error, operation) {
      const kind = api.errorKind(error);
      if (kind === 'rate-limit') { setStatus('Too many requests. Please try again later.'); return; }
      if (operation !== 'start' && kind === 'token-expired') { expiredSession(); return; }
      if (operation === 'verify' && (kind === 'wrong-code' || kind === 'attempts' || kind === 'code-expired')) {
        if (kind === 'wrong-code') failedAttempts++;
        clearCode();
        if (kind === 'attempts' || failedAttempts >= 3) {
          issued = null;
          showError('We Could Not Validate Your Code', 'We could not validate your code. Please close this window and request a new code.');
        } else if (kind === 'code-expired') {
          issued = null;
          setStatus('Your code has expired. Please request a new code.');
        } else setStatus('That code is incorrect. Please try again.');
        return;
      }
      if (kind === 'quote-limit') { showError('Quote Request Limit Reached', 'You have reached the quote request limit for the last 24 hours. Please try again later.'); return; }
      if (kind === 'pricing') { clearAuthorization(); showError('Pricing Is Unavailable', 'Dealer pricing is no longer available for this boat. Please contact a specialist.'); return; }
      setStatus(operation === 'email' ? 'We could not email your quote. Please try again.' : 'We could not complete your request. Please try again.');
    }
    async function run(operation, callback) {
      if (busy || destroyed || panel.hidden) return false;
      const id = ++generation;
      requestController = new AbortController();
      const signal = requestController.signal;
      const current = () => !destroyed && generation === id && !panel.hidden && stateManager.currentView === view;
      busy = true; syncButtons();
      setStatus({ start: 'Sending your code…', verify: 'Verifying your code and preparing your quote…', email: 'Emailing your quote…' }[operation] || 'Please wait…');
      try { await callback(signal, current); }
      catch (error) { if (current()) handleError(error, operation); }
      finally {
        if (generation === id) {
          busy = false; requestController = null; syncButtons();
          if (current()) pendingFocus?.focus();
          pendingFocus = null;
        }
      }
      return true;
    }
    function requestCode(channel) {
      return run('start', async (signal, current) => {
        const destination = channel === 'sms' ? api.toE164(values.phone, values.dialCode) : values.email;
        await client.start(channel, destination, signal);
        if (!current()) return;
        clearAuthorization(); clearCode(); failedAttempts = 0;
        values.channel = channel;
        issued = { channel, destination };
        writeValues(); render('verification', true);
      });
    }
    function onFieldInput(event) {
      const input = event.target;
      const name = input.name;
      if (busy) return;
      if (name === 'channel' && ['email', 'sms'].includes(input.value)) values.channel = input.value;
      else if (name === 'dialCode') values.dialCode = input.value;
      else if (fieldNames.includes(name)) {
        if (name === 'consent') values.consent = input.checked;
        else { input.value = normalizeField(name, input.value); values[name] = input.value; }
      } else return;
      setStatus(''); updateValidation();
    }
    function submitDetails(event) {
      event.preventDefault();
      fieldNames.forEach((name) => touched.add(name));
      if (busy || !updateValidation().valid) return;
      if (!context.stockNumber || !context.storeCode) { setStatus('Dealer pricing is unavailable for this boat. Please contact a specialist.'); return; }
      return requestCode(values.channel);
    }
    function onDigitInput(event) {
      const index = digits.indexOf(event.target);
      if (index < 0 || busy) return;
      const numbers = String(event.target.value).replace(/\D/g, '');
      digits[index].value = numbers.charAt(0);
      for (let offset = 1; offset < numbers.length && index + offset < digits.length; offset++) digits[index + offset].value = numbers.charAt(offset);
      if (numbers.length && index < digits.length - 1) digits[Math.min(5, index + numbers.length)].focus();
      setStatus(''); syncButtons();
    }
    function onDigitPaste(event) {
      const index = digits.indexOf(event.target);
      if (index < 0 || busy) return;
      event.preventDefault();
      const numbers = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6 - index);
      for (let offset = 0; offset < numbers.length; offset++) digits[index + offset].value = numbers[offset];
      digits[Math.min(5, index + numbers.length)]?.focus();
      setStatus(''); syncButtons();
    }
    async function submitLead(current = () => true) {
      const retry = leadRetry;
      const message = panel.querySelector('[data-dealer-lead-status]');
      if (retry) { retry.hidden = true; retry.disabled = true; }
      if (!quote || context.hubspot?.enabled !== true) return;
      const currentQuote = quote;
      const isCurrentQuote = () => current() && !destroyed && !panel.hidden && currentStep === 'quote' && quote === currentQuote;
      try {
        let hutk;
        try { hutk = /(?:^|;\s*)hubspotutk=([^;]+)/.exec(documentRef.cookie || '')?.[1]; } catch (_) { hutk = null; }
        const payload = api.buildLeadPayload({
          quote: currentQuote, values: { ...values }, context,
          consentText: panel.querySelector('[data-dealer-consent-text]')?.textContent.replace(/\s+/g, ' ').trim(),
          consentLabel: panel.querySelector('[data-dealer-consent-label]')?.textContent.replace(/\s+/g, ' ').trim(),
          pageUri: windowRef.location.href, pageName: documentRef.title, hutk,
        });
        const receipt = await leadSender.submit(currentQuote.session_id, payload);
        if (receipt.status === 'not-configured') throw new Error('Contact submission is not configured.');
        if (isCurrentQuote() && message) { message.textContent = ''; message.hidden = true; }
      } catch (_) {
        if (!isCurrentQuote()) return;
        if (message) { message.textContent = 'Your pricing is ready, but we could not submit your contact request. Please try again.'; message.hidden = false; }
        if (retry) { retry.hidden = false; retry.disabled = false; }
      }
    }
    function verifyCode() {
      if (verify?.disabled) return;
      return run('verify', async (signal, current) => {
        if (!hasToken()) {
          if (!issued) { expiredSession(); return; }
          const response = await client.verify(issued.channel, issued.destination, digits.map((digit) => digit.value).join(''), signal);
          if (!current()) return;
          if (!response.access_token || !(Number(response.expires_in) > 0)) throw new Error('The verification response was incomplete.');
          authToken = response.access_token;
          tokenExpiresAt = now() + Math.min(Number(response.expires_in), 86400) * 1000;
          failedAttempts = 0;
        }
        const response = await client.unlock({
          store_code: context.storeCode, stock_number: context.stockNumber,
          contact_first_name: values.firstName.trim(), contact_last_name: values.lastName.trim(),
          contact_email: values.email, contact_phone: api.toE164(values.phone, values.dialCode),
        }, authToken, signal);
        if (!current()) return;
        if (!api.validQuote(response, context.stockNumber)) throw new Error('The quote response was incomplete.');
        if (response.verified_channel.type !== issued.channel || response.verified_channel.value.toLowerCase() !== issued.destination.toLowerCase()) throw new Error('The verified contact does not match this session.');
        quote = response;
        clearCode(); render('quote', true);
        void submitLead();
      });
    }
    function emailQuote() {
      if (email?.disabled || !quote) return;
      if (!hasToken()) { expiredSession(); syncButtons(); return; }
      return run('email', async (signal, current) => {
        await client.email(quote.quote_id, authToken, signal);
        if (current()) setStatus('Your quote has been emailed.');
      });
    }
    function downloadQuote() {
      if (download?.disabled || !quote) return;
      const url = api.safeUrl(quote.pdf.url);
      if (!url) return;
      const anchor = documentRef.createElement('a');
      anchor.href = url; anchor.target = '_blank'; anchor.rel = 'noopener noreferrer';
      anchor.download = String(quote.pdf.filename || 'Dealer_Pricing_Quote.pdf');
      anchor.click();
    }
    function resetForVariant() {
      cancelPending(); clearAuthorization(); clearCode(); issued = null; failedAttempts = 0;
      currentStep = 'details'; setStatus('');
      const selected = context.variants?.find((item) => String(item.id) === String(variantId));
      if (selected?.stockNumber) context.stockNumber = selected.stockNumber;
      if (syncEligibility() && !panel.hidden) render('details', true);
    }
    listen(form, 'input', onFieldInput);
    listen(form, 'change', onFieldInput);
    listen(form, 'focusout', (event) => { touched.add(event.target.name); updateValidation(); });
    listen(form, 'beforeinput', (event) => { if (event.data === ' ' && !event.target.value) event.preventDefault(); });
    listen(form, 'submit', submitDetails);
    for (const digit of digits) {
      listen(digit, 'input', onDigitInput); listen(digit, 'paste', onDigitPaste);
      listen(digit, 'keydown', (event) => {
        const index = digits.indexOf(digit);
        if (event.key === 'Backspace' && !digit.value && index > 0) digits[index - 1].focus();
        if (event.key === 'ArrowLeft' && index > 0) { event.preventDefault(); digits[index - 1].focus(); }
        if (event.key === 'ArrowRight' && index < 5) { event.preventDefault(); digits[index + 1].focus(); }
      });
    }
    listen(verify, 'click', verifyCode);
    listen(resend, 'click', () => { if (!busy) return requestCode(values.channel); });
    listen(switchChannel, 'click', () => { if (!busy) return requestCode(values.channel === 'email' ? 'sms' : 'email'); });
    listen(download, 'click', downloadQuote); listen(email, 'click', emailQuote);
    listen(leadRetry, 'click', () => { if (!leadRetry.disabled) return submitLead(() => !destroyed && !panel.hidden && currentStep === 'quote'); });
    listen(panel.querySelector('[data-dealer-close]'), 'click', close);
    listen(windowRef, 'resize', syncEligibility);
    listen(windowRef, 'popstate', () => {
      const next = new URL(windowRef.location.href).searchParams.get('variant') || context.selectedVariantId;
      if (String(next) !== String(variantId)) { variantId = next; resetForVariant(); }
    });
    listen(documentRef, 'variant:changed', (event) => {
      const variant = event.detail?.variant;
      const stored = context.variants?.find((item) => String(item.id) === String(variant?.id));
      if (!stored) return;
      const changed = String(variant.id) !== String(variantId);
      variantId = variant.id;
      if (variant.sku) stored.stockNumber = variant.sku;
      if (typeof variant.price === 'number' && Number.isFinite(variant.price)) { stored.hasPrice = variant.price > 0; stored.priceCents = variant.price; }
      if (changed) resetForVariant();
      else syncEligibility();
    });
    syncEligibility(); syncButtons();
    return Object.freeze({
      open, close, hide, syncEligibility,
      back,
      ownsView: (candidate) => candidate === view,
      captureViewData: () => ({ step: currentStep, values: { ...values } }),
      restoreView: (snapshot) => {
        if (snapshot.view !== view || !canShow()) return false;
        cancelPending();
        Object.assign(values, snapshot.viewData?.values || {});
        writeValues(); clearCode();
        return render(snapshot.viewData?.step === 'quote' && quote && hasToken() ? 'quote' : issued ? 'verification' : 'details');
      },
      destroy: () => {
        if (destroyed) return false;
        destroyed = true; hide(); clearCode(); clearAuthorization(); issued = null;
        cleanups.splice(0).forEach((cleanup) => cleanup());
        fieldNames.forEach((name) => { values[name] = name === 'consent' ? false : ''; });
        writeValues();
        return true;
      },
    });
  }
  return Object.freeze({ createDealerPricingController, isEligible, validateDetails, normalizeField, maskDestination });
});
